import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { isDesktop } from '../lib/platform.ts'

/**
 * `/ir?para=/work-items/<id>` — um link https que abre no Orbind Desktop e,
 * sem ele, segue para a web (SPEC-20261104-1214, pedido do Cristian: os links
 * dos Itens trazidos pelo Claude precisam ser clicáveis e cair no Desktop
 * quando ele existe; o esquema `orbind://` sozinho não é clicável em chats).
 *
 * Como funciona: a página dispara o deep link `orbind://<caminho>` (o shell
 * 0.3.7+ abre a tela pedida, ver AvisosDeNotificacao) e, passado um instante
 * sem o sistema ter trocado de app, segue para o mesmo caminho aqui na web.
 * Quem já está DENTRO do Desktop só navega. Só caminhos internos (começando
 * por "/") são aceitos — nada de mandar a pessoa para fora.
 */
const ESPERA_PELO_DESKTOP_MS = 1500

function caminhoSeguro(bruto: string | null): string {
  if (!bruto || !bruto.startsWith('/') || bruto.startsWith('//')) return '/home'
  return bruto
}

export function IrPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const para = useMemo(() => caminhoSeguro(params.get('para')), [params])
  const deepLink = `orbind://${para.replace(/^\/+/, '')}`
  const [tentou, setTentou] = useState(false)

  useEffect(() => {
    if (isDesktop()) {
      navigate(para, { replace: true })
      return
    }
    // dispara o deep link; se o Desktop pegar, o navegador perde o foco e a
    // aba fica para trás — não há como saber ao certo, então seguimos para a
    // web depois de um instante, salvo se a aba foi escondida (o app abriu)
    let escondeu = false
    const aoEsconder = () => {
      if (document.visibilityState === 'hidden') escondeu = true
    }
    document.addEventListener('visibilitychange', aoEsconder)
    window.location.href = deepLink
    setTentou(true)
    const timer = setTimeout(() => {
      if (!escondeu) navigate(para, { replace: true })
    }, ESPERA_PELO_DESKTOP_MS)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', aoEsconder)
    }
  }, [deepLink, navigate, para])

  return (
    <main
      className="ob-root"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--ob-background)',
        color: 'var(--ob-text)',
        fontFamily: 'var(--ob-font-body)',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 420, textAlign: 'center', display: 'grid', gap: 12 }}>
        <strong style={{ fontFamily: 'var(--ob-font-display)', fontSize: 18, color: 'var(--ob-text-strong)' }}>
          {tentou ? 'Abrindo no Orbind Desktop…' : 'Abrindo…'}
        </strong>
        <span style={{ color: 'var(--ob-text-muted)', fontSize: 14 }}>
          Se o Desktop não abrir, você segue para a web em instantes.
        </span>
        <span style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a className="ob-btn ob-btn--secondary" href={deepLink}>
            Abrir no Desktop
          </a>
          <button type="button" className="ob-btn ob-btn--primary" onClick={() => navigate(para, { replace: true })}>
            Continuar na web
          </button>
        </span>
      </div>
    </main>
  )
}
